import {Divider, MenuItem, MenuList, Link, Box, Typography} from '@material-ui/core'
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight'
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft'
import classNames from 'classnames'
import debounce from 'lodash/debounce'
import React from 'react'
import {NavLink as NavLinkComponent} from 'react-router-dom'

import {useNavLinkStyles} from '../styles'
import {BurgerMenuProps, NavItem} from '../types'

import {MenuLinksBox} from './MenuLinksBox'

interface NavMenuLinksProps
  extends Pick<BurgerMenuProps, 'navItems' | 'data-test-id' | 'analytics'> {
  onSelect?: (selectedItem?: NavItem) => void
  isMobile?: boolean
  keepPointerEvents?: boolean
  submenuTitle?: string
}

export const NavMenuLinks: React.FC<NavMenuLinksProps> = ({
  navItems = [],
  onSelect,
  analytics,
  'data-test-id': dataTestId,
  isMobile = true,
  keepPointerEvents,
  submenuTitle
}) => {
  const {
    activeLink,
    disabledLink,
    link,
    verticalLink,
    menuItemLabel,
    divider,
    pointerEvents,
    noPointerEvents
  } = useNavLinkStyles()

  const [drillInOpen, setDrillInOpen] = React.useState(-1)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = React.useState<{index: number; item: NavItem} | null>(null)

  const debounceHandleMouseLeave = React.useCallback(
    debounce(() => handleMouseLeave(), 500),
    []
  )

  const debounceHandleMouseEnter = React.useCallback(
    debounce((newValue: number) => handleMouseEnter(newValue), 500),
    []
  )
  React.useEffect(() => {
    return () => {
      setDrillInOpen(-1)
    }
  }, [])

  const handleMouseEnter = (index: number) => setDrillInOpen(index)
  const handleMouseLeave = () => setDrillInOpen(-1)
  
  if (isMobile && mobileSubmenuOpen) {
    const showMobileSubmenuHeader = mobileSubmenuOpen.item.dataTestId !== 'nav-link-configurations-hconnect-nested'
    return (
      <Box>
        {showMobileSubmenuHeader && (
        <Box 
          display="flex" 
          alignItems="center" 
          px={2}
          pt={2}
          pb={1}
        >
          <KeyboardArrowLeft
            fontSize="small"
            style={{color: '#016AD4', cursor: 'pointer'}}
            onClick={() => setMobileSubmenuOpen(null)}
            data-test-id="submenu-back-button"
          />
          <Typography 
            variant="subtitle1" 
            align="center" 
            style={{
              flex: '1 0 0',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              color: 'rgba(0, 0, 0, 0.54)',
              wordWrap: 'break-word'
            }}
          >
            {mobileSubmenuOpen.item.label}
          </Typography>
        </Box>
        )}
        <NavMenuLinks
          navItems={mobileSubmenuOpen.item.subItems || []}
          data-test-id={`${dataTestId}-submenu`}
          onSelect={onSelect}
          analytics={analytics}
          isMobile
          keepPointerEvents={keepPointerEvents}
        />
      </Box>
    )
  }
  
  return (
    <MenuList disablePadding data-test-id={dataTestId}>
      {submenuTitle && (
        <Box px={2} pt={2} pb={1} display="flex" alignItems="flex-start">
          <KeyboardArrowLeft
            fontSize="small"
            style={{color: '#016AD4', cursor: 'pointer'}}
            data-test-id="desktop-submenu-back-button"
            onClick={() => setDrillInOpen(-1)}
          />
          <Typography
            variant="subtitle1"
            align="center"
            style={{
              flex: '1 0 0',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              color: 'rgba(0, 0, 0, 0.54)',
              wordWrap: 'break-word',
            }}
          >
            {submenuTitle}
          </Typography>
        </Box>
      )}
      {/* eslint-disable-next-line complexity */}
      {navItems.flatMap((navItem, index) => [
        <Link
          underline="none"
          key={`${navItem.label}-${navItem.url}`}
          component={navItem.url ? NavLinkComponent : 'div'}
          to={navItem.url || undefined}
          activeClassName={navItem.url ? classNames(activeLink, {
            [noPointerEvents]: keepPointerEvents
              ? false
              : !(navItem.subItems && navItem.subItems.length > 0),
            [pointerEvents]: keepPointerEvents
              ? true
              : navItem.subItems && navItem.subItems.length > 0 && !isMobile
          }) : undefined}
          isActive={navItem.isActive || undefined}
          className={classNames(link, {[disabledLink]: !!navItem.isDisabled})}
          data-test-id={
            navItem.dataTestId &&
            (dataTestId ? `${navItem.dataTestId}-${dataTestId}` : navItem.dataTestId)
          }
          onClick={() => {
            if (isMobile && navItem.subItems && navItem.subItems.length > 0) {
              setMobileSubmenuOpen({index, item: navItem})
            } else {
              onSelect && onSelect(navItem)
              analytics && analytics(navItem.url)
            }
          }}
        >
          <MenuItem
            className={verticalLink}
            onMouseEnter={() => !isMobile && debounceHandleMouseEnter(index)}
          >
            <span className={menuItemLabel}>{navItem.label}</span>
            {navItem.subItems && navItem.subItems.length > 0 ? (
              <KeyboardArrowRight fontSize="inherit" style={{marginLeft: 'auto'}} />
            ) : null}
          </MenuItem>
          {(() => {
            const drillInItem = !isMobile && drillInOpen >= 0 && drillInOpen < navItems.length ? navItems[drillInOpen] : undefined
            const drillInSubItems = drillInItem?.subItems
            if (!drillInItem || !drillInSubItems || drillInSubItems.length === 0) return null
            return (
            <MenuLinksBox
              onMouseLeave={debounceHandleMouseLeave}
              sx={{
                position: 'absolute',
                left: '100%',
                top: 0,
                boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.1), 0px 4px 12px rgba(0, 0, 0, 0.12)',
                paddingLeft: 'auto',
                paddingRight: 'auto'
              }}
            >
              <NavMenuLinks
                navItems={drillInSubItems}
                data-test-id={`${dataTestId}-drillIn`}
                onSelect={onSelect}
                isMobile={isMobile}
                keepPointerEvents={keepPointerEvents}
                submenuTitle={
                  drillInItem?.dataTestId === 'nav-link-messages' ||
                  drillInItem?.dataTestId === 'nav-link-configurations-hconnect-nested'
                    ? undefined
                    : drillInItem?.label
                }
              />
            </MenuLinksBox>
            )
          })()}
        </Link>,
        index < navItems.length - 1 ? (
          <Divider
            key={`${navItem.label}-${navItem.url}-divider`}
            orientation="horizontal"
            color="soft"
            className={divider}
          />
        ) : null
      ])}
    </MenuList>
  )
}
